import {FormGroup} from '@angular/forms';

import {
    AccountInfoInputModel,
    CompanyExtendedModel,
    CountryModel,
    IndividualExtendedModel,
    LinkedAccountInputModel,
    LinkedAccountModel,
    PaymentMethodsEnum
} from '@spa/api';

import {InArrayValidator} from '@spa/validators';

import {Helper} from '../../../../shared/classes';

import {BankAccountTypesEnum, CountryCodesEnum} from '../../enums';

import {
    BankAccountsFormAccountNumberSelector,
    BankAccountsFormAccountTypeSelector,
    BankAccountsFormBankAddressSelector,
    BankAccountsFormBankCountrySelector,
    BankAccountsFormBankNameSelector,
    BankAccountsFormBicSelector,
    BankAccountsFormControls,
    BankAccountsFormControlsInterface,
    BankAccountsFormCurrencySelector,
    BankAccountsFormIbanSelector,
    BankAccountsFormSortCodeSelector,
    BankAccountsFormStatusSelector,
    CryptoNetworkCodeSelector,
    CryptoWalletAddressSelector,
    CryptoWalletCodeSelector,
    CryptoWalletNameSelector,
    CryptoWalletTypeSelector,
} from '../../../../../assets/descriptors';

export class BankAccountsTrait {
    private constructor() {
    }

    private static restoreNonCryptoSharedControls(
        bankAccount: LinkedAccountModel,
        controls: BankAccountsFormControlsInterface,
        floatingLabelNever: boolean,
        countries: CountryModel[]
    ): void {
        if (!controls.bankName) {
            controls.bankName = BankAccountsFormControls(floatingLabelNever).bankName;
            controls.bankName.value = !bankAccount ? null : bankAccount.accountInfo.bankName;
        }
        if (!controls.currency) {
            controls.currency = BankAccountsFormControls(floatingLabelNever).currency;
            controls.currency.value = !bankAccount ? null : bankAccount.currencyCode;
        }
        if (!controls.beneficiaryBankCountry) {
            controls.beneficiaryBankCountry = BankAccountsFormControls(floatingLabelNever).beneficiaryBankCountry;
            const countriesIds = countries.map(c => c.id);
            controls.beneficiaryBankCountry.extendValidators(InArrayValidator(countriesIds));
            controls.beneficiaryBankCountry.countries = countries;
            controls.beneficiaryBankCountry.value = !bankAccount ? null : bankAccount.accountInfo.bankCountryId;
        }
        if (!controls.beneficiaryBankAddress) {
            controls.beneficiaryBankAddress = BankAccountsFormControls(floatingLabelNever).beneficiaryBankAddress;
            controls.beneficiaryBankAddress.value = !bankAccount ? null : bankAccount.accountInfo.bankAddress;
        }
    }

    static fixControlsOnInit(
        bankAccount: LinkedAccountModel,
        controls: BankAccountsFormControlsInterface,
        countries: CountryModel[]
    ): void {
        if (bankAccount && bankAccount.accountInfo.accountNumber && bankAccount.accountInfo.bic) {
            controls.iban = null;
            controls.sortCode = null;
            controls.accountType.value = BankAccountTypesEnum.BankAccount;
            controls.crypto_wallet_address = null;
            controls.crypto_wallet_name = null;
            controls.crypto_network_code = null;
            controls.crypto_code = null;
            controls.crypto_wallet_type = null;
        } else if (bankAccount && bankAccount.accountInfo.accountNumber && bankAccount.accountInfo.sortCode) {
            controls.iban = null;
            controls.bic = null;
            controls.accountType.value = BankAccountTypesEnum.SortCode;
            controls.beneficiaryBankCountry.disabled = true;
            controls.crypto_wallet_address = null;
            controls.crypto_wallet_name = null;
            controls.crypto_network_code = null;
            controls.crypto_code = null;
            controls.crypto_wallet_type = null;
        } else if (bankAccount && bankAccount.accountInfo.cryptoNetworkCode) {
            controls.accountType.value = BankAccountTypesEnum.CryptoWallet;
            controls.iban = null;
            controls.accountNumber = null;
            controls.sortCode = null;
            controls.bic = null;
            controls.currency = null;
            controls.beneficiaryBankCountry = null;
            controls.beneficiaryBankAddress = null;
            controls.bankName = null;
        } else {
            controls.accountNumber = null;
            controls.sortCode = null;
            controls.accountType.value = BankAccountTypesEnum.Iban;
            controls.crypto_wallet_address = null;
            controls.crypto_wallet_name = null;
            controls.crypto_network_code = null;
            controls.crypto_code = null;
            controls.crypto_wallet_type = null;
        }

        if (controls.beneficiaryBankCountry) {
            const countriesIds = countries.map((country: CountryModel) => country.id);
            controls.beneficiaryBankCountry.extendValidators(InArrayValidator(countriesIds));
            controls.beneficiaryBankCountry.countries = countries;
        }
    }

    static initControlValue(
        linkedAccount: LinkedAccountModel,
        controlName: string,
        controlValue: string | number
    ): string | number {
        if (controlName === BankAccountsFormAccountTypeSelector) {
            return controlValue;
        }

        if (!linkedAccount) {
            return null;
        }

        switch (controlName) {
            case BankAccountsFormIbanSelector:
                return linkedAccount.accountInfo.iban;
            case BankAccountsFormAccountNumberSelector:
                return linkedAccount.accountInfo.accountNumber;
            case BankAccountsFormSortCodeSelector:
                return linkedAccount.accountInfo.sortCode;
            case BankAccountsFormBankNameSelector:
                return linkedAccount.accountInfo.bankName;
            case BankAccountsFormBicSelector:
                return linkedAccount.accountInfo.bic;
            case BankAccountsFormBankCountrySelector:
                return linkedAccount.accountInfo.bankCountryId;
            case BankAccountsFormBankAddressSelector:
                return linkedAccount.accountInfo.bankAddress;
            case BankAccountsFormStatusSelector:
                return linkedAccount.status;
            case BankAccountsFormCurrencySelector:
                return linkedAccount.currencyCode;
            case CryptoWalletAddressSelector:
                return linkedAccount.accountInfo.cryptoWalletAddress;
            case CryptoWalletNameSelector:
                return linkedAccount.accountInfo.cryptoWalletName;
            case CryptoNetworkCodeSelector:
                return linkedAccount.accountInfo.cryptoNetworkCode;
            case CryptoWalletCodeSelector:
                return linkedAccount.accountInfo.cryptoCode;
            case CryptoWalletTypeSelector:
                return linkedAccount.accountInfo.cryptoWalletType;
            default:
                return null;
        }
    }

    static fixControls(
        accountType: number,
        bankAccount: LinkedAccountModel,
        controls: BankAccountsFormControlsInterface,
        floatingLabelNever: boolean,
        countries: CountryModel[] = []
    ): void {
        switch (accountType) {
            case BankAccountTypesEnum.Iban:
                controls.bic = BankAccountsFormControls(floatingLabelNever).bic;
                controls.bic.value = !bankAccount ? null : bankAccount.accountInfo.bic;

                controls.iban = BankAccountsFormControls(floatingLabelNever).iban;
                controls.iban.value = !bankAccount ? null : bankAccount.accountInfo.iban;

                controls.sortCode = null;
                controls.accountNumber = null;
                controls.crypto_wallet_address = null;
                controls.crypto_wallet_name = null;
                controls.crypto_network_code = null;
                controls.crypto_code = null;
                controls.crypto_wallet_type = null;
                BankAccountsTrait.restoreNonCryptoSharedControls(bankAccount, controls, floatingLabelNever, countries);
                break;
            case BankAccountTypesEnum.BankAccount:
                controls.bic = BankAccountsFormControls(floatingLabelNever).bic;
                controls.bic.value = !bankAccount ? null : bankAccount.accountInfo.bic;

                controls.accountNumber = BankAccountsFormControls(floatingLabelNever).accountNumber;
                controls.accountNumber.value = !bankAccount ? null : bankAccount.accountInfo.accountNumber;

                controls.sortCode = null;
                controls.iban = null;
                controls.crypto_wallet_address = null;
                controls.crypto_wallet_name = null;
                controls.crypto_network_code = null;
                controls.crypto_code = null;
                controls.crypto_wallet_type = null;
                BankAccountsTrait.restoreNonCryptoSharedControls(bankAccount, controls, floatingLabelNever, countries);
                break;
            case BankAccountTypesEnum.SortCode:
                controls.sortCode = BankAccountsFormControls(floatingLabelNever).sortCode;
                controls.sortCode.value = !bankAccount ? null : bankAccount.accountInfo.sortCode;

                controls.accountNumber = BankAccountsFormControls(floatingLabelNever).accountNumber;
                controls.accountNumber.value = !bankAccount ? null : bankAccount.accountInfo.accountNumber;

                controls.beneficiaryBankCountry.value = CountryCodesEnum.UnitedKingdom;
                controls.beneficiaryBankCountry.disabled = true;

                controls.bic = null;
                controls.iban = null;
                controls.crypto_wallet_address = null;
                controls.crypto_wallet_name = null;
                controls.crypto_network_code = null;
                controls.crypto_code = null;
                controls.crypto_wallet_type = null;
                BankAccountsTrait.restoreNonCryptoSharedControls(bankAccount, controls, floatingLabelNever, countries);
                break;
            case BankAccountTypesEnum.CryptoWallet:
                controls.crypto_wallet_address = BankAccountsFormControls(floatingLabelNever).crypto_wallet_address;
                controls.crypto_wallet_address.value = !bankAccount ? null : bankAccount.accountInfo.cryptoWalletAddress;

                controls.crypto_wallet_name = BankAccountsFormControls(floatingLabelNever).crypto_wallet_name;
                controls.crypto_wallet_name.value = !bankAccount ? null : bankAccount.accountInfo.cryptoWalletName;

                controls.crypto_network_code = BankAccountsFormControls(floatingLabelNever).crypto_network_code;
                controls.crypto_network_code.value = !bankAccount ? null : bankAccount.accountInfo.cryptoNetworkCode;

                controls.crypto_code = BankAccountsFormControls(floatingLabelNever).crypto_code;
                controls.crypto_code.value = !bankAccount ? null : bankAccount.accountInfo.cryptoCode;

                controls.crypto_wallet_type = BankAccountsFormControls(floatingLabelNever).crypto_wallet_type;
                controls.crypto_wallet_type.value = !bankAccount ? null : bankAccount.accountInfo.cryptoWalletType;

                controls.iban = null;
                controls.accountNumber = null;
                controls.sortCode = null;
                controls.bic = null;
                controls.currency = null;
                controls.beneficiaryBankCountry = null;
                controls.beneficiaryBankAddress = null;
                controls.bankName = null;
                break;
        }
    }

    static createBakAccountInputModel(
        client: IndividualExtendedModel | CompanyExtendedModel,
        isCorporateClient: boolean,
        form: FormGroup,
        file?: File
    ): LinkedAccountInputModel {
        const accountType = form.value[BankAccountsFormAccountTypeSelector];

        let accountInfo: AccountInfoInputModel;

        if (accountType === BankAccountTypesEnum.CryptoWallet) {
            accountInfo = new AccountInfoInputModel(!client ? null : client.name, null, null, null);
            accountInfo.crypto_wallet_address = form.value[CryptoWalletAddressSelector];
            accountInfo.crypto_wallet_name = form.value[CryptoWalletNameSelector];
            accountInfo.crypto_network_code = form.value[CryptoNetworkCodeSelector];
            accountInfo.crypto_code = form.value[CryptoWalletCodeSelector];
            accountInfo.crypto_wallet_type = form.value[CryptoWalletTypeSelector];
        } else {
            accountInfo = new AccountInfoInputModel(
                !client ? null : client.name,
                form.value[BankAccountsFormBankNameSelector],
                form.value[BankAccountsFormBankCountrySelector],
                form.value[BankAccountsFormBankAddressSelector]
            );

            switch (accountType) {
                case BankAccountTypesEnum.Iban:
                    accountInfo.bic = Helper.compactString(form.value[BankAccountsFormBicSelector]);
                    accountInfo.iban = Helper.compactString(form.value[BankAccountsFormIbanSelector]);
                    break;
                case BankAccountTypesEnum.BankAccount:
                    accountInfo.bic = Helper.compactString(form.value[BankAccountsFormBicSelector]);
                    accountInfo.account = Helper.compactString(form.value[BankAccountsFormAccountNumberSelector]);
                    break;
                case BankAccountTypesEnum.SortCode:
                    accountInfo.sortCode = Helper.compactString(form.value[BankAccountsFormSortCodeSelector]);
                    accountInfo.account = Helper.compactString(form.value[BankAccountsFormAccountNumberSelector]);
                    break;
            }
        }

        return new LinkedAccountInputModel(
            !isCorporateClient ? client.id : null,
            !isCorporateClient ? null : client.id,
            isCorporateClient,
            accountInfo,
            '',
            accountType === BankAccountTypesEnum.CryptoWallet ? null : form.value[BankAccountsFormCurrencySelector],
            file,
            accountType === BankAccountTypesEnum.CryptoWallet ? PaymentMethodsEnum.Crypto : PaymentMethodsEnum.BankTransfer
        );
    }

    static updateBankAccount(bankAccount: LinkedAccountModel, form: FormGroup): void {
        bankAccount.status = form.value[BankAccountsFormStatusSelector];

        const accountType = form.value[BankAccountsFormAccountTypeSelector];

        if (accountType !== BankAccountTypesEnum.CryptoWallet) {
            bankAccount.accountInfo.bankName = form.value[BankAccountsFormBankNameSelector];
            bankAccount.accountInfo.bankCountryId = form.value[BankAccountsFormBankCountrySelector];
            bankAccount.accountInfo.bankAddress = form.value[BankAccountsFormBankAddressSelector];
            bankAccount.currencyCode = form.value[BankAccountsFormCurrencySelector];
        }

        switch (accountType) {
            case BankAccountTypesEnum.CryptoWallet:
                bankAccount.accountInfo.cryptoWalletAddress = form.value[CryptoWalletAddressSelector];
                bankAccount.accountInfo.cryptoWalletName = form.value[CryptoWalletNameSelector];
                bankAccount.accountInfo.cryptoNetworkCode = form.value[CryptoNetworkCodeSelector];
                bankAccount.accountInfo.cryptoCode = form.value[CryptoWalletCodeSelector];
                bankAccount.accountInfo.cryptoWalletType = form.value[CryptoWalletTypeSelector];
                break;
            case BankAccountTypesEnum.Iban:
                bankAccount.accountInfo.sortCode = null;
                bankAccount.accountInfo.accountNumber = null;
                bankAccount.accountInfo.bic = Helper.compactString(form.value[BankAccountsFormBicSelector]);
                bankAccount.accountInfo.iban = Helper.compactString(form.value[BankAccountsFormIbanSelector]);
                break;
            case BankAccountTypesEnum.BankAccount:
                bankAccount.accountInfo.sortCode = null;
                bankAccount.accountInfo.iban = null;
                bankAccount.accountInfo.bic = Helper.compactString(form.value[BankAccountsFormBicSelector]);
                bankAccount.accountInfo.accountNumber = Helper.compactString(form.value[BankAccountsFormAccountNumberSelector]);
                break;
            case BankAccountTypesEnum.SortCode:
                bankAccount.accountInfo.bic = null;
                bankAccount.accountInfo.iban = null;
                bankAccount.accountInfo.accountNumber = Helper.compactString(form.value[BankAccountsFormAccountNumberSelector]);
                bankAccount.accountInfo.sortCode = Helper.compactString(form.value[BankAccountsFormSortCodeSelector]);
                bankAccount.accountInfo.bankCountryId = CountryCodesEnum.UnitedKingdom;
                break;
        }
    }
}
